import { useContext } from 'react'
import { ProductsContext } from '../../context/products.context'
import { Tab } from '@headlessui/react'
import { CipsElement } from '../../components/CipsElement'
import CipsComments from './CipsComments'
import { SchoolEnrolledTable } from '../../components/SchoolEnrolledTable'
import { JobsTable } from '../../components/JobsTable'
import CipCatalog from '../../components/CipCatalog'
import CipEquity from '../../components/CipEquity'
import CipEnrollmentsOthers from '../../components/CipEnrollmentsOthers'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}


export default function CipSnippet() {
  const { init, cipEquityData, cipDoc, currentCip, jobs, userDefaults, workforceMenu } = useContext(ProductsContext)

  // For totals (program area names), get the list of CIP codes that belong to this program area
  const programAreaCips = (workforceMenu &&
    workforceMenu.assignProgramClusterCips
      .map(e => e[currentCip])
      .filter(Boolean)
  )

  // Check if any of the job's aligned CIPs match the program area's CIPs
  function findCommonCipsForProgramArea(alignedCips, programCips) {
    if (!programCips || programCips.length === 0) return false
    return alignedCips.some(item => programCips.flat().includes(item))
  }

  // For regular CIPs, cipDetails exists. For totals, only cipEnrollment exists.
  const isLoaded = cipDoc?.cipDetails || cipDoc?.cipEnrollment

  if (!isLoaded) {
    // Show spinner + skeleton
    return (
      <div className="text-gray-500">

      </div>
    )
  }

  // Determine display name: use cipDetails.cipName for regular CIPs, or currentCip for totals
  const cipName = cipDoc?.cipDetails?.cipName || currentCip
  const isTotal = !cipDoc?.cipDetails

  // Once doc is ready, show real CIP content
  return (
    <div className=''>
      <div className='border-b border-gray-400 py-2 my-2'>
        {!isTotal && (
          <span className="text-2xl font-light px-2">
            {currentCip.toString().replace(/\B(?=(\d{4})+(?!\d))/g, '.')}
          </span>
        )}
        <span className="text-2xl font-bold leading-7 mt-7 text-slate-900">
          {cipName}
        </span>
        {isTotal && (
          <span className="text-xl font-light ml-2 text-slate-600">
            Totals
          </span>
        )}
      </div>

      <CipCatalog cip={currentCip} cipCatalog={cipDoc} />

      <Tab.Group as="div">
        <div className="border-b border-gray-200 place-content-evenly">
          <Tab.List>
            <Tab className={({ selected }) =>
              classNames(
                selected ? 'text-indigo-600 border-indigo-600' : 'text-gray-900 border-transparent',
                'flex-1 whitespace-nowrap py-2 px-4 border-b-2 text-base font-medium'
              )
            }>
              Enrollments
            </Tab>
            <Tab className={({ selected }) =>
              classNames(
                selected ? 'text-indigo-600 border-indigo-600' : 'text-gray-900 border-transparent',
                'flex-1 whitespace-nowrap py-2 px-4 border-b-2 text-base font-medium'
              )
            }>
              Labor Market Info
            </Tab>
            <Tab className={({ selected }) =>
              classNames(
                selected ? 'text-indigo-600 border-indigo-600' : 'text-gray-900 border-transparent',
                'flex-1 whitespace-nowrap py-2 px-4 border-b-2 text-base font-medium'
              )
            }>
              Equity
            </Tab>
            {/* <Tab>Notes</Tab> */}
          </Tab.List>
        </div>

        <Tab.Panels>
          <div className='w-6xl mx-auto'>

            <Tab.Panel>
              {init?.cipEnrolled && (
                <SchoolEnrolledTable 
                  school={init.cipEnrolled.find(e => e.cipcode === currentCip)} 
                  districtName={init.districtName}
                />
              )}
              <CipEnrollmentsOthers
                cip={currentCip}
                cipCatalog={cipDoc}
                defaultConsortium={userDefaults?.defaultConsortium}
              />
            </Tab.Panel>

            <Tab.Panel>
              {jobs?.socData && (
                <CipsElement
                  items={jobs.socData
                    .map(e => {
                      if (currentCip.length === 6) {
                        // Regular CIP code - direct match
                        return Array.isArray(e.alignedCip) && e.alignedCip.includes(currentCip) ? e : null
                      } else {
                        // Program area totals - check if any aligned CIPs match the program area's CIPs
                        return Array.isArray(e.alignedCip) && findCommonCipsForProgramArea(e.alignedCip, programAreaCips) ? e : null
                      }
                    })
                    .filter(Boolean)}
                  resourceName="jobs"
                  itemComponent={JobsTable}
                />
              )}
            </Tab.Panel>

            <Tab.Panel>
              {cipEquityData?.length > 0 && init.schoolDemo && (
                <CipEquity
                  cip={currentCip}
                  cipEquityData={cipEquityData}
                  schoolDemographic={init.schoolDemo}
                />
              )}
            </Tab.Panel>

            <Tab.Panel>
              <CipsComments cip={currentCip} />
            </Tab.Panel>

          </div>
        </Tab.Panels>
      </Tab.Group>
    </div>
  )
}